import React from 'react';

import { groupBy, orderBy } from 'lodash-es';
import pluralize from 'pluralize';

import {
	BarDatum,
	EVINCED_SEVERITIES_IDS,
	EvincedColors,
	IssueTypesCount,
	PieDatum,
	TypesCountBySeverity
} from '@evinced-private/ui-common';

import { ISeverityCountType } from 'src/common/components/mobile-scans-impacted-by-severity-chart/MobileScansImpactedBySeverityChart';
import { formatTextToId } from 'src/common/helpers/IdFormatterHelper';
import { MOBILE_ISSUE_SEVERITY_LIST } from 'src/common/helpers/MobileQueriesHelper';
import { generateUUID } from 'src/common/helpers/UUIDHelper';
import {
	MobileIssueTypeCount,
	ScansImpactedBySeverityChartData
} from 'src/common/types/MobileSingleSessionTypes';

const { severitiesPatterns } = EvincedColors;

const SEVERITY_COLORS = {
	[EVINCED_SEVERITIES_IDS.CRITICAL]: 'var(--critical)',
	[EVINCED_SEVERITIES_IDS.SERIOUS]: 'var(--serious)',
	[EVINCED_SEVERITIES_IDS.MODERATE]: 'var(--moderate)',
	[EVINCED_SEVERITIES_IDS.MINOR]: 'var(--minor)',
	[EVINCED_SEVERITIES_IDS.NEEDS_REVIEW]: 'var(--needs-review)',
	[EVINCED_SEVERITIES_IDS.BEST_PRACTICE]: 'var(--best-practice)'
} as const;

export const customSrSummaryRenderer = (
	barData: BarDatum[],
	label: string,
	itemType: string
): JSX.Element => (
	<ul aria-label={label}>
		{barData.map(({ type, count }) => {
			const text = `${type}: ${count} ${pluralize(itemType, Number(count))}`;
			return <li key={generateUUID()}>{text}</li>;
		})}
	</ul>
);

export const mobileSeverityPatterns = severitiesPatterns.map((pattern) => ({
	...pattern,
	color: SEVERITY_COLORS[pattern.id] || ''
}));

export const prepareDataForScansImpactedBySeverityChart = (
	severitiesCount: ISeverityCountType[]
): ScansImpactedBySeverityChartData[] => {
	return severitiesCount
		.filter(({ scanCount }) => scanCount > 0)
		.map(({ id, severity, name, scanCount }) => ({
			id,
			label: name,
			count: scanCount,
			severity
		}));
};

export const prepareDataForBreakdownBySeverityPieChart = (
	severitiesCount: ISeverityCountType[]
): PieDatum[] => {
	// Calculate total count for percentages
	const totalCount = severitiesCount.reduce((sum, severity) => sum + severity.count, 0);
	// Filter out zero counts and map to pie chart data format
	return severitiesCount.map((severityCount) => {
		// Find the matching severity pattern to get the color
		const severityPattern = mobileSeverityPatterns.find(
			(pattern) => pattern.id === severityCount.id
		);

		return {
			id: severityCount.id,
			label: severityCount.name,
			value: totalCount > 0 ? Math.round((severityCount.count / totalCount) * 100) : 0,
			count: severityCount.count,
			severity: severityCount.severity,
			color: severityPattern?.color || ''
		};
	});
};

export const prepareDataForIssueTypesChart = (issueTypes: MobileIssueTypeCount[]): BarDatum[] => {
	const totalIssues = issueTypes.length > 0 ? issueTypes[0].totalIssues : 0;

	// Calculate percentages and take top 5
	const processedData = issueTypes
		.map((item) => ({
			...item,
			percentage: totalIssues > 0 ? Math.round((item.count / totalIssues) * 100) : 0
		}))
		.slice(0, 5); // Take top 5 issue types

	return processedData.map(({ type, severity, count, percentage }) => {
		const color = SEVERITY_COLORS[severity];

		return {
			id: type,
			label: type,
			count,
			value: percentage,
			type,
			severity,
			severityId: severity,
			color
		};
	});
};

export const prepareDataForTotalIssuesBreakdownByIssueTypeChart = (
	issueTypes: MobileIssueTypeCount[]
): TypesCountBySeverity[] => {
	const bySeverity = groupBy(issueTypes, 'severity');

	return MOBILE_ISSUE_SEVERITY_LIST.filter((severityObj) => bySeverity[severityObj.key]) // Only include severities that have data
		.map((severityObj): TypesCountBySeverity => {
			const items = bySeverity[severityObj.key];
			const issueTypesCount: IssueTypesCount[] = orderBy(items, 'count', 'desc').map((item) => ({
				id: formatTextToId(`${severityObj.key}-${item.type}`),
				typeID: item.type,
				typeLabel: item.type,
				severityId: severityObj.key,
				severityLabel: severityObj.name,
				count: item.count
			}));

			return {
				severity: severityObj.name,
				issueTypesCount
			};
		});
};
